import React, { Component } from 'react'
import PropTypes from 'prop-types'
import ClassNames from 'classnames'
import optimizedWindowResize from '../../../common/lib/optimized-window-resize'
import viewableSlides from '../../../common/lib/viewable-slides'

class Slider extends Component {
  constructor(props) {
    super(props)

    this.defaultTransitionDelay = '0s'
    this.defaultTransitionDuration = '.7s'
    this.defaultTransitionTimingFunction = 'cubic-bezier(0.5, 0, 0.1, 1)'

    this.ref_Mask = React.createRef()
    this.ref_Slides = React.createRef()
    this.totalSlides = this.props.slides.length
    this.totalViewableSlides = viewableSlides()

    this.state = {
      isAtBeginning: true,
      isAtEnd: false,
      needButtons: (this.totalSlides > this.totalViewableSlides),
      totalSlidesToMount: this.totalViewableSlides * 2,
      totalViewableItems: 0,
      transitionDelay: this.defaultTransitionDelay,
      transitionDuration: this.defaultTransitionDuration,
      transitionTimingFunction: this.defaultTransitionTimingFunction,
      translateXAmount: 0,
      viewableGroupIndex: 0,
    }

    this.calcFirstVisibleSlide = this.calcFirstVisibleSlide.bind(this)
    this.handleNextClick = this.handleNextClick.bind(this)
    this.handlePreviousClick = this.handlePreviousClick.bind(this)
    this.maintainSliderPosition = this.maintainSliderPosition.bind(this)
  }

  calcFirstVisibleSlide() {
    const $_mask = this.ref_Mask.current
    const $_slides = this.ref_Slides.current
    const $_slidesChildren = $_slides.children

    let firstVisibleSlideId = 0

    const maskLeft = $_mask.getBoundingClientRect().left

    for (let i = 0; i < $_slidesChildren.length; i++) {
      const $_slide = $_slidesChildren[i]
      const slideLeft = $_slide.getBoundingClientRect().left
      const slideWidth = $_slide.offsetWidth
      const rangePadding = (slideWidth / 2)

      if (slideLeft < (maskLeft + rangePadding) && slideLeft > (maskLeft - rangePadding)) {
        firstVisibleSlideId = i
      }
    }

    return firstVisibleSlideId
  }

  handleNextClick(event) {
    this.totalViewableSlides = viewableSlides()

    const $_mask = this.ref_Mask.current
    const $_slides = this.ref_Slides.current

    if (!$_mask || !$_slides) {
      return
    }

    const currentTranslateXAmount = this.state.translateXAmount
    const isAtBeginning = false
    const maskLeft = $_mask.getBoundingClientRect().left
    const maskRight = $_mask.getBoundingClientRect().right
    const maskWidth = $_mask.offsetWidth
    const slidesWidth = $_slides.scrollWidth
    const totalSlides = this.totalSlides
    const totalSlidesToMount = this.state.totalSlidesToMount
    const totalViewableSlides = viewableSlides()

    let isAtEnd = false

    // TODO
    // figure out a better way
    // to compensate for scroll bar width
    const scrollBarCompensator = 100

    const groupIndexLimit = Math.round(totalSlides / totalViewableSlides) - 1
    const translateXLimit = -((slidesWidth - maskRight) + maskLeft)

    // calculate new translate amount
    let newTranslateXAmount = currentTranslateXAmount - maskWidth

    // keep new translate amount within limit
    if (newTranslateXAmount < (translateXLimit - scrollBarCompensator)) {
      isAtEnd = true
      newTranslateXAmount = translateXLimit
    }

    // calculate new group index
    let newGroupIndex = -(Math.round(newTranslateXAmount / maskWidth))

    // keep new group index within limit
    if (newGroupIndex > groupIndexLimit) {
      newGroupIndex = groupIndexLimit
    }

    // calculate total slides to mount
    let newTotalSlidesToMount = (totalSlidesToMount + totalViewableSlides)

    // keep total slides to mount within limit
    if (newTotalSlidesToMount > totalSlides) {
      newTotalSlidesToMount = totalSlides
    }

    this.setState({
      isAtBeginning: isAtBeginning,
      isAtEnd: isAtEnd,
      totalSlidesToMount: newTotalSlidesToMount,
      transitionDelay: this.defaultTransitionDelay,
      transitionDuration: this.defaultTransitionDuration,
      transitionTimingFunction: this.defaultTransitionTimingFunction,
      translateXAmount: newTranslateXAmount,
      viewableGroupIndex: newGroupIndex,
    })

    event.stopPropagation()
  }

  handlePreviousClick(event) {
    this.totalViewableSlides = viewableSlides()

    const $_mask = this.ref_Mask.current

    if (!$_mask) {
      return
    }

    const currentTranslateXAmount = this.state.translateXAmount
    const groupIndexLimit = 0
    const isAtEnd = false
    const maskWidth = $_mask.offsetWidth
    const translateXLimit = 0

    let isAtBeginning = false

    // calculate new translate amount
    let newTranslateXAmount = currentTranslateXAmount + maskWidth

    // keep translate amount within limit
    if (newTranslateXAmount > translateXLimit) {
      newTranslateXAmount = translateXLimit
    }

    // calculate new group index
    let newGroupIndex = -(Math.round(currentTranslateXAmount / maskWidth)) - 1

    // keep new group index within limit
    if (newGroupIndex <= groupIndexLimit) {
      isAtBeginning = true
      newGroupIndex = groupIndexLimit
    }

    this.setState({
      isAtBeginning: isAtBeginning,
      isAtEnd: isAtEnd,
      transitionDelay: this.defaultTransitionDelay,
      transitionDuration: this.defaultTransitionDuration,
      transitionTimingFunction: this.defaultTransitionTimingFunction,
      translateXAmount: newTranslateXAmount,
      viewableGroupIndex: newGroupIndex,
    })

    event.stopPropagation()
  }

  maintainSliderPosition() {
    this.setState({
      needButtons: (this.totalSlides > viewableSlides()),
    })

    const isAtBeginning = this.state.isAtBeginning

    if (isAtBeginning === false) {
      const $_mask = this.ref_Mask.current
      const $_slides = this.ref_Slides.current

      if (!$_mask || !$_slides) {
        return
      }

      const currentTotalViewableSlides = viewableSlides()
      const currentViewableGroupIndex = this.state.viewableGroupIndex
      const maskLeft = $_mask.getBoundingClientRect().left
      const maskRight = $_mask.getBoundingClientRect().right
      const maskWidth = $_mask.offsetWidth
      const previousTotalViewableSlides = this.totalViewableSlides
      const slidesWidth = $_slides.scrollWidth

      let newTranslateXAmount = 0

      const singleSlideWidth = (maskWidth / currentTotalViewableSlides)
      const previousGroupWidth = (singleSlideWidth * previousTotalViewableSlides)
      const translateXLimit = -((slidesWidth - maskRight) + maskLeft)

      let calculatedTranslateXAmount = (previousGroupWidth * (currentViewableGroupIndex))

      newTranslateXAmount = -(calculatedTranslateXAmount)

      // adjust translate amount if at end of slides
      if (newTranslateXAmount <= translateXLimit) {
        newTranslateXAmount = -(slidesWidth - maskWidth)
      }

      console.log('this.totalSlides: ', this.totalSlides)
      console.log('currentTotalViewableSlides: ', currentTotalViewableSlides)

      this.setState({
        transitionDelay: '0s',
        transitionDuration: '.2s',
        transitionTimingFunction: 'linear',
        translateXAmount: newTranslateXAmount,
      })
    }
  }

  componentDidMount() {
    const $_slides = this.ref_Slides.current

    $_slides.addEventListener('transitionend', (event) => {
      if (event.target.classList.contains('slider__slides')) {
        const firstVisibleSlideId = this.calcFirstVisibleSlide()

        this.props.setSelectedTitle(firstVisibleSlideId)
      }
    })

    optimizedWindowResize.add('maintainSliderPosition', () => {
      if (!this.props.disabled) {
        this.maintainSliderPosition()
      }
    })
  }

  componentWillUnmount() {
    optimizedWindowResize.remove('maintainSliderPosition')
  }

  render() {
    const styles = {
      slides: {
        transform: `translate3d(${this.state.translateXAmount}px, 0, 0)`,
        transitionDelay: this.state.transitionDelay,
        transitionDuration: this.state.transitionDuration,
        transitionProperty: 'transform',
        transitionTimingFunction: this.state.transitionTimingFunction,
      },
    }

    const previousButtonClassNames = ClassNames({
      'slider__button': true,
      'slider__button--previous': true,
      'slider__button--inactive': this.state.isAtBeginning,
      'slider__button--poster-open': this.props.posterOpened,
    })

    const nextButtonClassNames = ClassNames({
      'slider__button': true,
      'slider__button--next': true,
      'slider__button--inactive': this.state.isAtEnd,
      'slider__button--poster-open': this.props.posterOpened,
    })

    const slides = this.props.slides.slice(0, this.state.totalSlidesToMount)

    return (
      <div className="slider">
        <div ref={this.ref_Mask} className="slider__mask slider__mask--inactive">
          <div ref={this.ref_Slides} className="slider__slides" style={styles.slides}>
            {slides}
          </div>
        </div>
        {this.state.needButtons && !this.props.disabled &&
        <React.Fragment>
          <button className={previousButtonClassNames} onClick={this.handlePreviousClick}>
            <div className="slider__button-icon-wrapper">
              <svg className="slider__button-icon" viewBox="0 0 24 24">
                <path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z" />
              </svg>
            </div>
          </button>
          <button className={nextButtonClassNames} onClick={this.handleNextClick}>
            <div className="slider__button-icon-wrapper">
              <svg className="slider__button-icon" viewBox="0 0 24 24">
                <path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z" />
              </svg>
            </div>
          </button>
        </React.Fragment>
        }
      </div>
    )
  }
}

Slider.propTypes = {
  disabled: PropTypes.bool,
  posterOpened: PropTypes.bool,
  setSelectedTitle: PropTypes.func,
  slides: PropTypes.array,
}

Slider.defaultProps = {
  disabled: false,
  posterOpened: false,
}

export default Slider



// WEBPACK FOOTER //
// ./src/desktop/components/slider/index.js